import EmptyResults from 'components/EmptyResults'
import { Button, Col, Grid, Icon } from 'gu-ui-kit'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useParams } from 'react-router-dom'
import { Banner } from './subcomponents/Banner'
import { Chart } from './subcomponents/Chart'
import WalletHeader from './subcomponents/WalletHeader'
import { useWallets } from 'modules/Finance/Wallets'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import Transaction from 'modules/Finance/Wallets/subcomponents/Transaction'
import WalletsList from 'modules/Finance/Wallets/subcomponents/WalletsList'
import clsx from 'clsx'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { useSelector } from 'react-redux'
import styles from 'modules/Finance/Wallets/Wallets.module.scss'
import { usePromisedPayment } from 'modules/Finance/PromisedPayment/hooks/usePromisedPayment'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'

const Wallets = () => {
  const { activeWalletID, wallets: { data: wallets = [], loaded } = {} } = useSelector(
    (state) => state['finance/wallets'],
  )
  const pathGen = useGeneratePathI18n()
  const { t } = useTranslation()
  const { setActiveWalletID, resetWallets, getWallets } = useWallets()
  const { getTakenPromisedPayment } = usePromisedPayment()
  const history = useHistory()
  const breakpoint = useBreakpoint()
  const { walletID, i18n } = useParams()
  const [conf] = useLocalStorage('wallets-list', {})

  useEffect(() => {
    if (!loaded || activeWalletID || !wallets.length) {
      return
    }

    let currentWalletId = null

    if (walletID && wallets.find(({ id }) => id === +walletID)) {
      currentWalletId = +walletID
    }

    if (!walletID && Object.keys(conf).length > 0) {
      const pinnedWallets = Object.entries(conf)
        .filter(([id]) => wallets.find(({ id: walletId }) => +id === +walletId))
        .filter(([, { pinned = false }]) => pinned)

      if (pinnedWallets.length) {
        const currentWallet = pinnedWallets.reduce((firstPinnedWallet, currentWallet) => {
          return currentWallet[1].order < firstPinnedWallet[1].order
            ? currentWallet
            : firstPinnedWallet
        }, pinnedWallets[0])

        currentWalletId = currentWallet[0]
      }
    }
    setActiveWalletID(currentWalletId || wallets[0].id)
  }, [loaded, wallets, conf, activeWalletID])

  useEffect(() => {
    if (!activeWalletID) {
      return
    }
    const url = pathGen(ROUTES.FINANCE_WALLET, { walletID: activeWalletID })

    history.replace(url + history.location.search)
  }, [activeWalletID])

  useEffect(() => {
    getWallets()
  }, [i18n])

  useEffect(() => {
    getTakenPromisedPayment()

    return () => {
      walletID && resetWallets()
    }
  }, [])

  return (
    <>
      {wallets.length === 0 && loaded ? (
        <div className={'layout-tile'}>
          <EmptyResults
            header={t('wallets::start.header')}
            icon={<Icon name={'business-cards'} />}
            text={t('wallets::start.text')}
            actionButton={
              <Button
                component={Link}
                size="l"
                variant="primary"
                startIcon={<Icon name="plus" />}
                to={pathGen(ROUTES.FINANCE_PAYMENT_PROFILE_NEW)}
              >
                {t('wallets::start.create_first_profile')}
              </Button>
            }
          />
        </div>
      ) : (
        <Grid
          className={styles['wallets']}
          gap={16}
          wrap={false}
          direction={breakpoint.lg ? 'row' : 'column'}
        >
          {!breakpoint.lg && <Banner />}
          <Col
            flexGrow={0}
            className={clsx(styles['wallets__side'])}
          >
            <div className={'layout-tile'}>
              <WalletsList />
            </div>
          </Col>
          <Grid
            gap={16}
            direction={'column'}
            justifyContent={'start'}
            alignItems={'start'}
            flexGrow={1}
            className={clsx(styles['wallets__main-column'])}
          >
            {breakpoint.lg && <Banner />}
            <Grid
              className={clsx(styles['wallets__header'])}
              wrap={false}
              gap={16}
              justifyContent={'start'}
              direction={breakpoint.md ? 'row' : 'column'}
            >
              <Col
                flexGrow={1}
                className={clsx('layout-tile', styles['wallets__header-main'])}
              >
                <WalletHeader />
              </Col>
              <Col
                flexGrow={breakpoint.md ? 0 : 1}
                className={clsx(styles['wallets__header-side'], 'layout-tile')}
              >
                <Chart className={styles['wallets__chart']} />
              </Col>
            </Grid>

            <Col className={clsx('layout-tile', styles['wallets__transaction'])}>
              <Transaction />
            </Col>
          </Grid>
        </Grid>
      )}
    </>
  )
}
export default Wallets
